import React from "react";
import { Layout, Menu, Drawer } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import {
  DashboardOutlined,
  ShopOutlined,
  EnvironmentOutlined,
  OrderedListOutlined,
  TeamOutlined,
  BarChartOutlined,
  SecurityScanOutlined,
  GlobalOutlined,
  SafetyOutlined,
  BankOutlined,
  BellOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../contexts/AuthContext";

const { Sider } = Layout;

const Sidebar = ({ collapsed, setCollapsed, isMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Super Admin Menu Items
  const superAdminMenuItems = [
    {
      key: "/executive-dashboard",
      icon: <BarChartOutlined />,
      label: "Executive Dashboard",
    },
    {
      key: "/companies",
      icon: <GlobalOutlined />,
      label: "บริษัททั้งหมด",
    },
    {
      key: "/multi-company-admin",
      icon: <SafetyOutlined />,
      label: "ผู้ดูแลหลายบริษัท",
    },
    {
      key: "/guard-management",
      icon: <TeamOutlined />,
      label: "จัดการผู้ใช้งาน (รปภ.)",
    },
    {
      key: "/active-rounds",
      icon: <EyeOutlined />,
      label: "ติดตามรอบเดินตรวจปัจจุบัน",
    },
    {
      key: "/silence-requests",
      icon: <BellOutlined />,
      label: "คำขอหยุดเตือน",
    },
  ];

  // Company Admin Menu Items (kept for reference, not used anymore)
  // Now using dynamic getMenuItems() with visibility filtering

  // Helper function to check permission for multi-company admin
  const hasPermission = (permission) => {
    if (user?.role === "super_admin") return true;
    if (user?.role === "owner") return true;
    if (user?.role === "multi_company_admin") {
      console.log(
        "hasPermission check:",
        permission,
        "permissions:",
        user?.company_permissions,
      );
      // Return false if company_permissions is not set yet (during switching)
      if (!user?.company_permissions) {
        console.log("No company permissions, returning false");
        return false;
      }
      const hasAccess = user?.company_permissions?.[`can_${permission}`] === 1;
      console.log(
        `can_${permission}:`,
        user?.company_permissions?.[`can_${permission}`],
        "result:",
        hasAccess,
      );
      return hasAccess;
    }
    if (user?.role === "supervisor") {
      return ["view_reports"].includes(permission);
    }
    return false;
  };

  // Dynamic menu items based on user role and permissions
  const getMenuItems = () => {
    if (user?.role === "super_admin") {
      return superAdminMenuItems;
    }

    if (user?.role === "multi_company_patrol") {
      return [
        {
          key: "/active-rounds",
          icon: <EyeOutlined />,
          label: "ติดตามรอบเดินตรวจปัจจุบัน",
        },
        {
          key: "/guard-management",
          icon: <TeamOutlined />,
          label: "จัดการผู้ใช้งาน (รปภ.)",
        },
        {
          key: "/silence-requests",
          icon: <BellOutlined />,
          label: "คำขอหยุดเตือน",
        },
      ];
    }

    if (user?.role === "supervisor") {
      return [
        {
          key: "/patrol-reports",
          icon: <BarChartOutlined />,
          label: "รายงานการเดินตรวจ",
        },
        {
          key: "/users",
          icon: <TeamOutlined />,
          label: "ข้อมูล รปภ.",
        },
      ];
    }

    // For multi-company admin and other roles - filter out items user doesn't have access to
    const allMenuItems = [
      {
        key: "/executive-dashboard",
        icon: <BarChartOutlined />,
        label: "Executive Dashboard",
        visible: true,
      },
      {
        key: "/patrol-reports",
        icon: <BarChartOutlined />,
        label: "รายงานการเดินตรวจ",
        visible: hasPermission("view_reports"),
      },
      {
        key: "/checkpoints",
        icon: <EnvironmentOutlined />,
        label: "จุดตรวจ",
        visible: hasPermission("manage_checkpoints"),
      },
      {
        key: "/patrol-rounds",
        icon: <OrderedListOutlined />,
        label: "รอบการเดินตรวจ",
        visible: hasPermission("manage_rounds"),
      },
      {
        key: "/users",
        icon: <TeamOutlined />,
        label: "ผู้ใช้และ รปภ.",
        visible: hasPermission("manage_users"),
      },
      {
        key: "/company",
        icon: <ShopOutlined />,
        label: "ข้อมูลบริษัท",
        visible: hasPermission("manage_users") || user?.role === "owner",
      },
      {
        key: "/active-rounds",
        icon: <EyeOutlined />,
        label: "ติดตามรอบเดินตรวจปัจจุบัน",
        visible: true,
      },
      {
        key: "/silence-requests",
        icon: <BellOutlined />,
        label: "คำขอหยุดเตือน",
        visible: true,
      },
    ];

    // Filter out items that are not visible and remove the 'visible' property
    return allMenuItems
      .filter((item) => item.visible)
      .map(({ visible, ...item }) => item);
  };

  const handleMenuClick = ({ key }) => {
    navigate(key);
    if (isMobile) {
      setCollapsed(true);
    }
  };

  const getHeaderTitle = () => {
    if (user?.role === "super_admin") {
      return "ผู้ดูแลระบบ (System Admin)";
    }
    return "ระบบเดินตรวจ (Guard Tour)";
  };

  const getHeaderBackground = () => {
    if (user?.role === "super_admin") {
      return "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
    }
    return "#001529";
  };

  const sidebarContent = (
    <>
      <div
        style={{
          height: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderBottom: "1px solid #f0f0f0",
          background: getHeaderBackground(),
          color: "white",
          position: "relative",
        }}
      >
        <SecurityScanOutlined style={{ fontSize: 24, marginRight: 8 }} />
        <span style={{ fontWeight: "bold", fontSize: 16 }}>
          {getHeaderTitle()}
        </span>
      </div>

      {user?.role === "super_admin" && (
        <div
          style={{
            padding: "12px 16px",
            background: "#f0f2f5",
            borderBottom: "1px solid #e8e8e8",
            fontSize: "12px",
            color: "#666",
            textAlign: "center",
            fontWeight: "500",
          }}
        >
          🔧 ผู้ดูแลระบบ (System Administrator)
        </div>
      )}

      <Menu
        mode="inline"
        selectedKeys={[location.pathname]}
        items={getMenuItems()}
        onClick={handleMenuClick}
        style={{
          marginTop: 0,
          height:
            user?.role === "super_admin"
              ? "calc(100% - 104px)"
              : "calc(100% - 64px)",
          borderRight: 0,
          fontSize: "14px",
        }}
      />
    </>
  );

  return isMobile ? (
    <Drawer
      placement="left"
      closable={false}
      onClose={() => setCollapsed(true)}
      open={!collapsed}
      bodyStyle={{ padding: 0 }}
      width={260}
      styles={{ body: { padding: 0 } }}
    >
      <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {sidebarContent}
      </div>
    </Drawer>
  ) : (
    <Sider
      width={260}
      theme="light"
      style={{
        overflow: "auto",
        height: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 1000,
        boxShadow: "2px 0 8px rgba(0,0,0,0.15)",
      }}
    >
      {sidebarContent}
    </Sider>
  );
};

export default Sidebar;
